import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import CircularLoader from "app/components/CircularLoader";
import { CheckboxActiveIcon, CheckIcon } from "app/components/Icons";
import CheckboxIcon from "app/components/Icons/CheckboxIcon";
import ProfileAvatar from "app/components/ProfileAvatar";
import MenuHeader from "app/containers/Features/components/MemberSelection/MenuHeader";
import {
  IMemberList,
  MemberMenuItemProps,
} from "app/containers/Features/components/MemberSelection/types";
import clsx from "clsx";
import React, { useCallback, useRef, useState } from "react";

const MenuItem: React.FC<MemberMenuItemProps<IMemberList[]>> = ({
  options,
  menuContentClassName,
  className,
  searchPlaceholder,
  onChange,
  align = "start",
  dropdownWidth,
  value,
  headingText,
  isFullWidth,
  isMultiSelect,
  handleSelectAll,
  hideRole,
  hideSearchIcon,
  headerAreaClassName,
  inputNoBorder,
  headingTextClassName,
  showSearch = true,
  showTooltipArrowIcon = false,
  arrowClassName,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  isExternalSearch = false,
  externalOnSearchChange,
  externalSearchValue,
  isMoreLoading = false,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const searchMembers = (members, keyword: string): IMemberList[] => {
    if (!keyword) return members;
    return members.filter(
      (member) =>
        member.name.toLowerCase().includes(keyword.toLowerCase()) ||
        member.role.toLowerCase().includes(keyword.toLowerCase()),
    );
  };
  const filteredMembers = searchMembers(options, search);
  const isAllSelected = Array.isArray(value) && value.length === options.length;

  const noDataFound = filteredMembers?.length === 0;

  const handleScroll = useCallback(() => {
    if (!ref.current || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = ref.current;
    if (scrollTop + clientHeight >= scrollHeight * 0.9) {
      onLoadMore?.();
    }
  }, [hasMore, isLoading, onLoadMore]);

  return (
    <DropdownMenu.Content
      align={align}
      sideOffset={5}
      side="bottom"
      style={{ width: isFullWidth ? dropdownWidth : undefined }}
      // className={`bg-white shadow-lg rounded-md z-50 w-64 border border-gray-100  py-2 ${className}`}
      className={`bg-white shadow-lg rounded-md z-50 w-64 border border-gray-100 py-2 overflow-visible ${className}`}
    >
      {showTooltipArrowIcon && (
        <DropdownMenu.Arrow
          className={clsx(
            "fill-white drop-shadow-sm stroke-secondary ",
            arrowClassName,
          )}
          strokeWidth={1}
          width={25}
          height={10}
        />
      )}

      <MenuHeader
        isMultiSelect={Boolean(isMultiSelect)}
        search={search}
        setSearch={setSearch}
        handleSelectAll={handleSelectAll}
        headingText={headingText}
        isAllSelected={isAllSelected}
        searchPlaceholder={searchPlaceholder}
        hideSearchIcon={hideSearchIcon as boolean}
        headerAreaClassName={headerAreaClassName}
        inputNoBorder={inputNoBorder}
        headingTextClassName={headingTextClassName}
        showSearch={showSearch}
        isExternalSearch={isExternalSearch}
        externalOnSearchChange={externalOnSearchChange}
        externalSearchValue={externalSearchValue}
      />

      <div
        className={clsx("overflow-auto max-h-[270px] ", menuContentClassName)}
        ref={ref}
        onScroll={handleScroll}
      >
        {isLoading ? (
          <div className="w-full flex items-center justify-center py-3">
            <CircularLoader size="md" />
          </div>
        ) : noDataFound ? (
          <div className="w-full flex items-center justify-center py-3">
            <span className="text-sm text-secondary">No options found</span>
          </div>
        ) : (
          <>
            {filteredMembers?.map((option) => {
              const isArray = Array.isArray(value);
              const isSelected = isArray
                ? value.includes(option._id)
                : value === option._id;
              return (
                <DropdownMenu.Item
                  key={option._id}
                  onClick={() => {
                    onChange(option);
                  }}
                  className={`cursor-pointer hover:outline-none hover:bg-gray-400 px-4 py-3 flex items-center gap-2  `}
                >
                  {isMultiSelect &&
                    (isSelected ? <CheckboxActiveIcon /> : <CheckboxIcon />)}
                  <div className="w-full flex flex-1 items-center gap-2 truncate ">
                    <ProfileAvatar
                      name={option.name}
                      avatar={option.avatar}
                      circleClassName=" !size-[25px] "
                    />
                    <div className="flex flex-col truncate w-full  ">
                      <span
                        className={clsx(
                          "text-sm font-normal text-black truncate",
                          value === option._id && " !font-medium ",
                        )}
                      >
                        {option.name}
                      </span>
                      {!hideRole && (
                        <span
                          className={clsx(
                            "text-xs font-normal text-secondary truncate ",
                          )}
                        >
                          {option.role}
                        </span>
                      )}
                    </div>
                  </div>
                  {!isMultiSelect && isSelected && (
                    <CheckIcon color="#5A8DF1" />
                  )}
                </DropdownMenu.Item>
              );
            })}
          </>
        )}
        {isMoreLoading && filteredMembers.length > 0 && (
          <div className="w-full flex items-center justify-center py-3">
            <CircularLoader size="sm" />
          </div>
        )}
      </div>
    </DropdownMenu.Content>
  );
};

export default MenuItem;
